import translate from "@/i18n/translate";
import "./Lightcast.scss";
import { formatNumber } from "../../../src/static/js/commons";
import { useEffect, useReducer } from "react";
import { sendRequest } from "@/common/http";
import WidgetLoader from "../common/elements/WidgetLoader";
import ApiError from "../common/elements/ApiError";
import {
  LaborForceBreakdown,
  LaborForceTrendsInterface,
  PopulationTrendsInterface,
  Timeframe,
  JobTrendsInterface,
  LaborForceTrend
} from "./Interface";

interface PopulationTrendsProps {
  populationTrends: PopulationTrendsInterface | null;
  timeframe: Timeframe | null;
}

interface JobTrendsProps {
  jobTrends: JobTrendsInterface | null;
  timeframe: Timeframe | null;
}

interface LaborForceTrendsProps {
  laborForceTrends: LaborForceTrend | null;
  timeframe: Timeframe | null;
}

interface TrendsState {
  populationTrends: PopulationTrendsInterface | null;
  jobTrends: JobTrendsInterface | null;
  laborForceTrends: LaborForceTrend | null;
  timeframe: Timeframe | null;
  loader: boolean;
  btError: boolean;
}

interface TrendsAction {
  type: string;
  lft: LaborForceTrendsInterface | null;
}

const getIncreaseDecrease = (growthState: boolean) => {
  return growthState
    ? translate("app.trends.increased")
    : translate("app.trends.decreased");
};

const getIncreasingDeclining = (inclined: boolean) => {
  return inclined
    ? translate("app.trends.increasing")
    : translate("app.trends.declining");
};

const getArrowIcon = (growthIncrease: boolean) => {
  return growthIncrease ? "mdi-arrow-up" : "mdi-arrow-down";
};
export interface TrendsProps {
  setLaborForceBreakdown: (lfb: LaborForceBreakdown | null) => void;
  onComponentLoad: (component: string) => void;
  setApiError: (lfbApiError: boolean) => void;
}

const reducer = (state: TrendsState, action: TrendsAction) => {
  switch (action.type) {
    case "setLaborForceBreakdownAndTrends":
      if (action.lft) {
        return {
          ...state,
          populationTrends: action.lft.populationTrends,
          jobTrends: action.lft.jobTrends,
          laborForceTrends: action.lft.laborForceTrends,
          timeframe: action.lft.timeframe,
          loader: false
        };
      } else {
        return {
          ...state,
          btError: true,
          loader: false
        };
      }

    default:
      return state;
  }
};

const initialState = {
  populationTrends: null,
  jobTrends: null,
  laborForceTrends: null,
  timeframe: null,
  loader: true,
  btError: false
};

export default function Trends({
  setLaborForceBreakdown,
  onComponentLoad,
  setApiError
}: TrendsProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  useEffect(() => {
    const applyResult = (result: LaborForceTrendsInterface) => {
      dispatch({ type: "setLaborForceBreakdownAndTrends", lft: result });
      let lfb = result ? result.laborForceBreakdown : null;
      setLaborForceBreakdown(lfb);
      onComponentLoad("trendsAndLaborforce");
    };
    const onError = () => {
      dispatch({
        type: "setLaborForceBreakdownAndTrends",
        lft: null
      });
      onComponentLoad("regionalContext");
      setApiError(true);
    };

    sendRequest(
      { uri: "/lightcast/labor_force_breakdown_and_trends", camelize: true },
      applyResult,
      onError
    );
  }, []);

  return (
    <div className="paper-card paper-card-height">
      <div className="header">
        <div className="info">
          <h3 className="title">{translate("app.trends.trendsWidgetTitle")}</h3>
          <p className="description -des">
            {translate("app.trends.trendsWidgetDescription")}
          </p>
        </div>
      </div>
      {!state.loader && !state.btError && (
        <div className="body wm-grid-row trends-widget">
          <PopulationTrends
            populationTrends={state.populationTrends}
            timeframe={state.timeframe}
          />
          <JobTrends jobTrends={state.jobTrends} timeframe={state.timeframe} />
          <LaborForceTrends
            laborForceTrends={state.laborForceTrends}
            timeframe={state.timeframe}
          />

          <UnemploymentTrends
            laborForceTrends={state.laborForceTrends}
            timeframe={state.timeframe}
          />
        </div>
      )}
      {state.loader && (
        <WidgetLoader loaderContainerClasses="loader" loaderClasses="content" />
      )}
      {state.btError && <ApiError />}
    </div>
  );
}

export const PopulationTrends = ({
  populationTrends,
  timeframe
}: PopulationTrendsProps) => {
  return (
    <div className="wm-grid-col wm-grid-col-3 wm-grid-col-lg-3 wm-grid-col-md-10 wm-grid-col-sm-8 -trend">
      {populationTrends && (
        <div className="-trend-value">
          <div className="label-name">{translate("app.trends.population")}</div>
          <div className="-percentage flex-row">
            <i
              className={`-icon mdi -midnight ${getArrowIcon(
                populationTrends.inclined
              )}`}
            ></i>
            <div>{`${populationTrends.changePercentage}%`}</div>
          </div>
          <div>
            {timeframe &&
              translate("app.trends.populationTrendDescription", {
                populationFromDate: timeframe.pastYear,
                populationToDate: timeframe.currentYear,
                populationGrowthIncrease: getIncreaseDecrease(
                  populationTrends.inclined
                ),
                populationChangePercentage: populationTrends.changePercentage,
                overallStatus: getIncreasingDeclining(
                  populationTrends.inclined
                ),
                changeInPopulation: formatNumber(populationTrends.change)
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export const JobTrends = ({ jobTrends, timeframe }: JobTrendsProps) => {
  return (
    <div className="wm-grid-col wm-grid-col-3 wm-grid-col-lg-3 wm-grid-col-md-10 wm-grid-col-sm-8 -trend">
      {jobTrends && (
        <div className="-trend-value">
          <div className="label-name">{translate("app.trends.jobs")}</div>
          <div className="-percentage flex-row">
            <i
              className={`-icon mdi -midnight ${getArrowIcon(
                jobTrends.jobsGrowthIncrease
              )}`}
            ></i>
            <div>{`${jobTrends.jobsPercentage}%`}</div>
          </div>
          <div>
            {timeframe &&
              translate("app.trends.jobsTrendDescription", {
                jobsFromDate: timeframe.pastYear,
                jobsToDate: timeframe.currentYear,
                jobsGrowthIncrease: getIncreaseDecrease(
                  jobTrends.jobsGrowthIncrease
                ),
                jobsPercentage: jobTrends.jobsPercentage,
                jobsFromTotalCount: formatNumber(jobTrends.jobsFromTotalCount),
                jobsToTotalCount: formatNumber(jobTrends.jobsToTotalCount),
                JobsNationalRateGrowthIncrease: getIncreaseDecrease(
                  jobTrends.JobsNationalRateGrowthIncrease
                ),
                jobsNationalRatePercentage: jobTrends.jobsNationalRatePercentage
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export const LaborForceTrends = ({
  laborForceTrends,
  timeframe
}: LaborForceTrendsProps) => {
  return (
    <div className="wm-grid-col wm-grid-col-3 wm-grid-col-lg-3 wm-grid-col-md-10 wm-grid-col-sm-8 -trend">
      {laborForceTrends && (
        <div className="-trend-value">
          <div className="label-name">{translate("app.trends.laborForce")}</div>
          <div className="-percentage flex-row">
            <i
              className={`-icon mdi -midnight ${getArrowIcon(
                laborForceTrends.laborForceGrowthIncrease
              )}`}
            ></i>
            <div>{`${laborForceTrends.laborForcePercentage}%`}</div>
          </div>
          <div>
            {timeframe &&
              translate("app.trends.laborForceDescription", {
                laborForceFromDate: timeframe.pastYear,
                laborForceToDate: timeframe.currentYear,
                laborForceGrowthIncrease: getIncreaseDecrease(
                  laborForceTrends.laborForceGrowthIncrease
                ),
                laborForcePercentage: laborForceTrends.laborForcePercentage,
                laborForceGrowthPercentageFrom:
                  laborForceTrends.laborForceGrowthPercentageFrom,
                laborForceGrowthPercentageTo:
                  laborForceTrends.laborForceGrowthPercentageTo
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export const UnemploymentTrends = ({
  laborForceTrends,
  timeframe
}: LaborForceTrendsProps) => {
  return (
    <div className="wm-grid-col wm-grid-col-3 wm-grid-col-lg-3 wm-grid-col-md-10 wm-grid-col-sm-8">
      {laborForceTrends && (
        <div className="-trend-value">
          <div className="label-name">
            {translate("app.trends.unemployment")}
          </div>
          <div className="-percentage flex-row">
            <i
              className={`-icon mdi -midnight ${getArrowIcon(
                laborForceTrends.unemploymentGrowthIncrease
              )}`}
            ></i>
            <div>{`${laborForceTrends.unemploymentPercentage}%`}</div>
          </div>
          <div>
            {timeframe &&
              translate("app.trends.unemploymentDescription", {
                unemploymentFromDate: timeframe.pastYear,
                unemploymentToDate: timeframe.currentYear,
                unemploymentGrowthIncrease: getIncreaseDecrease(
                  laborForceTrends.unemploymentGrowthIncrease
                ),
                unemploymentPercentage: laborForceTrends.unemploymentPercentage,
                unemploymentGrowthPercentageFrom:
                  laborForceTrends.unemploymentGrowthPercentageFrom,
                unemploymentGrowthPercentageTo:
                  laborForceTrends.unemploymentGrowthPercentageTo
              })}
          </div>
        </div>
      )}
    </div>
  );
};
